import {
  ItineraryJourney,
  ItinerarySegment,
  Bag,
  Journey,
  Segment,
} from '../components/baggage2/CommonTypes';

interface Itinerary {
  journeys: ItineraryJourney[];
}

const parseDate = (dateString: string): number => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? 0 : date.getTime();
};

const sortSegments = (segments: ItinerarySegment[]): ItinerarySegment[] => {
  return [...segments].sort(
    (a, b) => parseDate(a.departure_time) - parseDate(b.departure_time)
  );
};

interface IntermediateJourney {
  journey_id: string; // Arbitrary ID for display
  journey_ids: string[]; // Actual IDs from the bag
  from: string;
  to: string;
  segments: ItinerarySegment[]; // Keep original segments for sorting and later mapping
  firstDepartureTimestamp: number; // For sorting intermediate journeys
}

export const getJourneysFromItineraries = (
  itineraries: Itinerary[],
  bags: Bag[]
): Journey[] => {
  const allItineraryJourneysMap = new Map<string, ItineraryJourney>();
  itineraries
    .flatMap((itinerary) => itinerary.journeys)
    .forEach((journey) => {
      allItineraryJourneysMap.set(journey.journey_id, journey);
    });

  const intermediateJourneys: IntermediateJourney[] = [];
  const uniqueJourneySetsMap = new Map<string, string[]>();

  // Step 1: Identify unique sets of journey IDs from all bags
  Object.values(bags).forEach((bag) => {
    // Determine the relevant journey IDs for this bag, prioritizing journey_ids array
    let relevantJourneyIds: string[] = [];
    if (
      bag.product_details.journey_ids &&
      bag.product_details.journey_ids.length > 0
    ) {
      relevantJourneyIds = bag.product_details.journey_ids;
    } else if ((bag.product_details as any).journey_id) {
      relevantJourneyIds = [(bag.product_details as any).journey_id];
    }

    if (relevantJourneyIds.length === 0) {
      return;
    }

    // Create a canonical key by sorting the IDs
    const sortedIds = [...relevantJourneyIds].sort();
    const key = JSON.stringify(sortedIds);

    // Store the original (relevant) IDs if this set is unique
    if (!uniqueJourneySetsMap.has(key)) {
      uniqueJourneySetsMap.set(key, relevantJourneyIds);
    }
  });

  // Step 2: Process each unique set of journey IDs
  uniqueJourneySetsMap.forEach((journeyIdsSet) => {
    const segmentsForSet: ItinerarySegment[] = [];
    const foundJourneyIdsForSet: string[] = [];

    journeyIdsSet.forEach((id) => {
      const itineraryJourney = allItineraryJourneysMap.get(id);
      if (itineraryJourney) {
        segmentsForSet.push(...itineraryJourney.segments);
        foundJourneyIdsForSet.push(id);
      }
      // Consider logging if an ID is not found
    });

    // Sort the combined segments chronologically for this unique set
    const sortedCombinedSegments = sortSegments(segmentsForSet);

    // Ensure we still have segments after potential filtering/sorting issues
    if (sortedCombinedSegments.length === 0) {
      return;
    }

    const firstSegment = sortedCombinedSegments[0];
    const lastSegment =
      sortedCombinedSegments[sortedCombinedSegments.length - 1];

    // Create an intermediate Journey object for this unique set
    const intermediateJourney: IntermediateJourney = {
      journey_id: foundJourneyIdsForSet[0] ?? 'merged-' + journeyIdsSet[0],
      journey_ids: foundJourneyIdsForSet,
      from: firstSegment.departure_airport,
      to: lastSegment.arrival_airport,
      segments: sortedCombinedSegments,
      firstDepartureTimestamp: parseDate(firstSegment.departure_time),
    };

    intermediateJourneys.push(intermediateJourney);
  });

  const sortedIntermediateJourneys = intermediateJourneys.sort(
    (a, b) => a.firstDepartureTimestamp - b.firstDepartureTimestamp
  );

  const finalJourneys: Journey[] = sortedIntermediateJourneys.map(
    (intermediate, index, arr) => {
      const flights: Segment[] = intermediate.segments.map((seg) => ({
        from: seg.departure_airport,
        to: seg.arrival_airport,
        fromCity: '',
        toCity: '',
        // DO NOT add departureDateTime here if it's not in the imported Segment type
      }));

      const journey: Journey = {
        journey_id: intermediate.journey_id,
        journey_ids: intermediate.journey_ids,
        from: intermediate.from,
        to: intermediate.to,
        fromCity: '',
        toCity: '',
        direction: '',
        flights: flights,
        isLastJourney: index === arr.length - 1,
      };

      if (journey.from === journey.to) {
        journey.direction = 'Roundtrip';
      } else if (index === 0) {
        journey.direction = 'Outbound';
      } else {
        journey.direction = 'Inbound';
      }
      return journey;
    }
  );

  return finalJourneys;
};
